 
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState } from 'react';
import { X, CreditCard, Calendar } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';

interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  userEmail?: string;
  onSuccess: () => void;
}

const plans = [
  { id: 'starter', name: 'Starter', price: 149 },
  { id: 'pro', name: 'Pro', price: 349 },
  { id: 'business', name: 'Business', price: 799 },
  { id: 'enterprise', name: 'Enterprise', price: 1999 },
];

const statuses = [
  { id: 'active', name: 'Ativo' },
  { id: 'trialing', name: 'Trial' },
  { id: 'past_due', name: 'Pendente' },
  { id: 'canceled', name: 'Cancelado' },
];

export function CreateSubscriptionModal({
  isOpen,
  onClose,
  userId,
  userEmail,
  onSuccess,
}: CreateSubscriptionModalProps) {
  const [formData, setFormData] = useState({
    user_id: userId || '',
    user_email: userEmail || '',
    plan: 'starter',
    status: 'active',
    trial_days: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchingUser, setSearchingUser] = useState(false);

  const handleSearchUser = async () => {
    if (!formData.user_email) return;

    setSearchingUser(true);
    try {
      // Buscar usuário por email
      const { data: { users }, error } = await supabase.auth.admin.listUsers();
      
      if (error) throw error;

      const user = users.find(u => u.email === formData.user_email);
      
      if (user) {
        setFormData(prev => ({ ...prev, user_id: user.id }));
        toast.success('Usuário encontrado!');
      } else {
        toast.error('Usuário não encontrado');
 
      }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error('Erro ao buscar usuário: ' + error.message);
    } finally {
      setSearchingUser(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.user_id) {
      toast.error('Selecione ou busque um usuário primeiro');
      return;
    }

    setLoading(true);
    try {
      // Verificar se já existe subscrição
      const { data: existing } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', formData.user_id)
        .maybeSingle();

      if (existing) {
        toast.error('Usuário já possui subscrição. Use a opção de editar.');
        setLoading(false);
        return;
      }

      const now = new Date();
      const periodEnd = new Date(now);
 
      periodEnd.setMonth(periodEnd.getMonth() + 1);

// eslint-disable-next-line prefer-const
      let subscriptionData: any = { // eslint-disable-line @typescript-eslint/no-explicit-any
        user_id: formData.user_id,
        plan: formData.plan,
        status: formData.status,
        current_period_start: now.toISOString(),
        current_period_end: periodEnd.toISOString(),
      };

      // Se tiver trial
      if (formData.trial_days > 0) {
        const trialEnd = new Date(now);
        trialEnd.setDate(trialEnd.getDate() + formData.trial_days);
        subscriptionData.trial_start = now.toISOString();
        subscriptionData.trial_end = trialEnd.toISOString();
        subscriptionData.status = 'trialing';
      }

      const { data, error } = await supabase
        .from('subscriptions')
        .insert(subscriptionData)
        .select()
        .single();

      if (error) throw error;

      toast.success('Subscrição criada com sucesso!');
      onSuccess();
      onClose();
      setFormData({
        user_id: '',
        user_email: '',
        plan: 'starter',
 
        status: 'active',
        trial_days: 0,
      });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      toast.error('Erro ao criar subscrição: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <CreditCard className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Criar Subscrição</h2>
              <p className="text-sm text-slate-400">Criar subscrição manualmente para um cliente</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* User Search */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email do Usuário *
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={formData.user_email}
                onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
                placeholder="usuario@email.com"
                className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-red-500"
                required
              />
              <button
                type="button"
                onClick={handleSearchUser}
                disabled={searchingUser || !formData.user_email}
                className="px-4 py-2.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-all disabled:opacity-50"
              >
                {searchingUser ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
            {formData.user_id && (
              <p className="mt-2 text-xs text-emerald-400">✓ Usuário encontrado</p>
            )}
          </div>

          {/* Plan */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Plano *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, plan: plan.id }))}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    formData.plan === plan.id
                      ? 'border-red-500 bg-red-500/10'
                      : 'border-white/10 hover:border-white/20 bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white">{plan.name}</span>
                    <span className="text-sm text-slate-400">R$ {plan.price}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Status *
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
            >
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          {/* Trial Days */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Dias de Trial (opcional)
            </label>
            <input
              type="number"
              min="0"
              max="30"
              value={formData.trial_days}
              onChange={(e) => setFormData(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 0 }))}
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
            />
            <p className="mt-1 text-xs text-slate-500">
              Se preenchido, o status será automaticamente definido como "Trial"
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !formData.user_id}
              className="px-4 py-2 bg-red-500 rounded-lg text-white font-medium hover:bg-red-600 transition-all disabled:opacity-50"
            >
              {loading ? 'Criando...' : 'Criar Subscrição'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
